/**
 * Feature Flags Configuration
 *
 * Centralized configuration for cross-framework feature flags and routing decisions.
 * Supports tenant-aware and environment-based feature toggling.
 */

export interface FeatureFlags {
  /** Enable React Directory instead of AngularJS */
  useReactDirectory: boolean;
  /** Enable React Patients module */
  useReactPatients: boolean;
  /** Enable React Encounters module */
  useReactEncounters: boolean;
  /** Enable React Reports module */
  useReactReports: boolean;
  /** Enable React HR module (TECHR migration) */
  useReactHR: boolean;
  /** Enable React Admin/Settings entrypoint */
  useReactAdminSettings: boolean;
  /** Fallback to AngularJS if React version fails */
  enableAngularFallback: boolean;
  /** Show migration indicators in UI */
  showMigrationIndicators: boolean;
}

export interface TenantConfig {
  /** Tenant-specific feature overrides */
  featureOverrides?: Partial<FeatureFlags>;
  /** Single user mode affects routing behavior */
  singleUser?: boolean;
  /** Environment-specific settings */
  environment?: 'development' | 'staging' | 'production';
  /** Tenant identifier for feature flag resolution */
  tenantId?: string;
}

/**
 * Default feature flags - can be overridden per tenant or environment
 */
const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  useReactDirectory: true, // Enable React Directory by default
  useReactPatients: true, // React Patients module (Phase 1 list views)
  useReactEncounters: false, // Keep AngularJS for now
  useReactReports: true, // Enable React Reports (Phase 4 complete)
  useReactHR: true, // Enable React HR module (TECHR migration complete)
  useReactAdminSettings: true, // Enable React Admin/Settings shell
  enableAngularFallback: true, // Always provide fallback
  showMigrationIndicators: true, // Show indicators in development
};

/**
 * Get feature flags for current tenant/environment context
 */
export const getFeatureFlags = (tenantConfig?: TenantConfig): FeatureFlags => {
  const baseFlags = { ...DEFAULT_FEATURE_FLAGS };

  // Apply environment-specific overrides
  if (tenantConfig?.environment === 'production') {
    // More conservative in production
    baseFlags.showMigrationIndicators = false;
  }

  if (tenantConfig?.environment === 'development') {
    // More aggressive feature enabling in development
    baseFlags.showMigrationIndicators = true;
  }

  // Apply tenant-specific overrides
  if (tenantConfig?.featureOverrides) {
    Object.assign(baseFlags, tenantConfig.featureOverrides);
  }

  return baseFlags;
};

/**
 * Get directory URL based on feature flags and tenant configuration
 */
export const getDirectoryUrl = (
  featureFlags: FeatureFlags,
  _tenantConfig?: TenantConfig
): string => {
  if (featureFlags.useReactDirectory) {
    return '/directory'; // React Router managed route
  }
  return '/directory/info'; // AngularJS Django route
};

/**
 * Get navigation URLs for all modules based on feature flags
 */
export const getNavigationUrls = (
  featureFlags: FeatureFlags,
  tenantConfig?: TenantConfig
) => {
  return {
    directory: getDirectoryUrl(featureFlags, tenantConfig),
    patients: featureFlags.useReactPatients ? '/patients' : '/patients/',
    // No AngularJS fallback: 1e7c694 removed /encounters/main. Encounters are React-only,
    // so useReactEncounters no longer gates this URL -- leaving the ternary in meant a
    // false flag pointed navigation at a route that 404s.
    encounters: '/encounters',
    reports: featureFlags.useReactReports ? '/reports/' : '/reports/',
    hr: featureFlags.useReactHR ? '/hr/employees' : '/hr/', // React vs AngularJS HR
    admin: featureFlags.useReactAdminSettings ? '/admin/settings' : '/admin/main',
  };
};

/**
 * Check if user should see React version based on permissions and feature flags
 */
export const shouldUseReactVersion = (
  module: 'directory' | 'patients' | 'encounters' | 'reports' | 'hr' | 'admin',
  featureFlags: FeatureFlags,
  _userPermissions?: string[],
  _tenantConfig?: TenantConfig
): boolean => {
  const moduleFlag = {
    directory: featureFlags.useReactDirectory,
    patients: featureFlags.useReactPatients,
    encounters: featureFlags.useReactEncounters,
    reports: featureFlags.useReactReports,
    hr: featureFlags.useReactHR,
    admin: featureFlags.useReactAdminSettings,
  }[module];

  // Check if module is enabled for React
  if (!moduleFlag) return false;

  // Additional checks can be added here for specific user permissions or tenant requirements
  // For now, if the feature flag is enabled, use React version
  return true;
};

export default getFeatureFlags;
